import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import {
  Check,
  ChevronUp,
  Loader2,
  Puzzle,
  Settings,
} from 'lucide-react';
import { useAuthContext } from '../contexts/auth-context';
import { useToast } from '../hooks/use-toast';
import { cn } from '../lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const roleLabels = {
  OWNER: 'Owner',
  ADMIN: 'Admin',
  MANAGER: 'Manager',
  MEMBER: 'Member',
} as const;

interface OrganizationMenuProps {
  isCollapsed: boolean;
}

export function OrganizationMenu({ isCollapsed }: OrganizationMenuProps) {
  const navigate = useNavigate();
  const {
    session,
    organizations,
    switchingOrganizationId,
    selectOrganization,
  } = useAuthContext();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);

  if (!session) return null;

  const current = session.organization;
  const currentRole = roleLabels[session.membership.role];
  const switching = switchingOrganizationId !== null;

  const handleSelect = async (organizationId: string) => {
    if (organizationId === current.id) return;
    setOpen(false);
    try {
      await selectOrganization(organizationId);
      navigate('/my-work', { replace: true });
    } catch (error) {
      toast({
        title: 'Could not switch organization',
        description:
          error instanceof Error ? error.message : 'Please try again.',
      });
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`Organization menu for ${current.name}`}
          className={cn(
            'flex min-h-11 w-full items-center gap-2 rounded-component px-2 py-1.5 text-left transition-smooth hover:bg-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20',
            isCollapsed && 'justify-center px-1',
          )}
          disabled={switching}
          title={current.name}
        >
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-component border border-border bg-card text-xs font-semibold text-foreground">
            {switching ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
            ) : (
              current.name.slice(0, 1).toUpperCase()
            )}
          </span>
          {!isCollapsed && (
            <>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-foreground">
                  {current.name}
                </span>
                <span className="block truncate text-xs text-muted-foreground">
                  {currentRole}
                </span>
              </span>
              <ChevronUp
                className={cn(
                  'h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform',
                  open && 'rotate-180',
                )}
              />
            </>
          )}
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="start"
        side="top"
        sideOffset={8}
        className="max-h-[min(28rem,var(--radix-dropdown-menu-content-available-height))] w-72 overflow-y-auto"
      >
        <div className="px-2 pb-1 pt-1.5">
          <p className="text-xs font-medium text-muted-foreground">Organizations</p>
        </div>
        {organizations.map((organization) => (
          <DropdownMenuItem
            key={organization.id}
            disabled={switching}
            onSelect={() => void handleSelect(organization.id)}
            className="min-h-11 gap-2"
          >
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-component border border-border bg-background text-xs font-semibold">
              {organization.name.slice(0, 1).toUpperCase()}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm text-foreground">
                {organization.name}
              </span>
              <span className="block text-xs text-muted-foreground">
                {roleLabels[organization.role]}
              </span>
            </span>
            {organization.id === current.id && (
              <Check className="h-4 w-4 shrink-0 text-primary" aria-label="Current organization" />
            )}
          </DropdownMenuItem>
        ))}

        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/settings#organization" className="min-h-11">
            <Settings className="mr-2 h-4 w-4" />
            Organization settings
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/integrations" className="min-h-11">
            <Puzzle className="mr-2 h-4 w-4" />
            Organization integrations
          </Link>
        </DropdownMenuItem>
        {organizations.length === 0 && (
          <div className="px-2 py-3 text-sm text-muted-foreground">
            Organization list unavailable
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
